import React, { useEffect, useMemo, useState } from 'react'
import { Button } from '../shared/Button'
import { SkeletonRows } from '../shared/Skeleton'
import { crmToast, SegTabs } from './crmUi'
import {
  getObConfig, getUsers, obAddHours, obAttachLink, obByLead, obCreateMeeting, obCustomTask, obDismissSuggestion, obFiles, obGetLink, obGoldPrice, obMarkPaid, obSendOverNotice, obSetStepTutorial, obUploadFile,
  getOnboarding, obMeetingNotes, obReset, obRotateToken, obSessions, obSetAbout, obSetCounter, obSetPlan, obSetStep, obSetSub, obStart, obUpdate, obUpdateMeeting,
  type ObConfig, type Onboarding, type ObSession, type ObSessionTask,
} from './crmApi'
import { ObRiskPanel, RISK_COLOR } from './ObRiskPanel'

/* ── Attachment pills (Lee, 2 Oct 2026): translucent chip with title, type
      and size; click opens in a new tab. Links (no size) show as LINK. ── */
const fmtSize = (n?: number | null): string => {
  if (n == null) return ''
  if (n >= 1048576) return `${Math.round((n / 1048576) * 10) / 10} MB`
  if (n >= 1024) return `${Math.round(n / 1024)} KB`
  return `${n} B`
}
const fileTypeOf = (name: string, size?: number | null): string => {
  if (size == null) return 'LINK'
  const ext = (name.split('.').pop() || '').toUpperCase()
  return ext && ext.length <= 5 && ext !== name.toUpperCase() ? ext : 'FILE'
}
/** Steps that take customer/staff attachments (Lee, 7 Oct 2026): anything the
 *  config marks with an Upload resource, plus 1.3 "We set up your current
 *  quote layout" (the proposal reference lives there). Covers: missing
 *  supplier price lists (0.1), current proposal (0.9), customer list (0.10),
 *  custom products sheet (1.2), quote layout (1.3), brand assets (7.1)… */
export const isUploadStep = (sx: { res?: string[] }, si: number, pi: number): boolean =>
  !!sx.res?.includes('Upload') || `${si}.${pi}` === '1.3'

const FilePill: React.FC<{ f: { name: string; url: string; size?: number | null } }> = ({ f }) => (
  <a
    href={f.url} target="_blank" rel="noreferrer"
    className="inline-flex items-center gap-1.5 max-w-full px-2 py-0.5 rounded-full border border-border bg-surface-tertiary/60 text-[10px] text-txt-secondary no-underline hover:bg-surface-hover hover:text-brand-600"
    title={`${f.name}${f.size != null ? ` · ${fmtSize(f.size)}` : ' · link'} — open in a new tab`}
  >
    <span className="font-semibold truncate max-w-[180px]">{f.name}</span>
    <span className="px-1 rounded bg-surface text-[8px] font-bold text-txt-tertiary border border-border shrink-0">{fileTypeOf(f.name, f.size)}</span>
    {f.size != null && <span className="text-txt-tertiary shrink-0">{fmtSize(f.size)}</span>}
  </a>
)
import { ObStageIcon } from './obStageIcons'

/**
 * ONBOARDING TAB on the lead (Lee, 28 Sep 2026 — Phase 3, from the sandbox).
 * Customer-link row · plan panel (awaiting: four plan cards with staff
 * actions; active: plan chip + hours bar + payment actions + owner) ·
 * Checkpoints (five accordions, checklist ~65% beside the stage's meetings
 * ~35%) · Bookings (timeline per stage, editable "What's been done",
 * transcript side-sheet stub until the Meet integration phase).
 */

const fmtD = (s?: string | null) => (s ? new Date(s).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' }) : '—')
/** "Covered on the call" list for a stage: side list, else the blocks' lists. */
const obCoveredList = (cfg: ObConfig, si: number): string[] => {
  const S: any = cfg.stages[si]
  if (!S) return []
  return S.side ? S.side.items : (S.blocks || []).flatMap((b: any) => b.covered || [])
}
const fmtT = (s?: string | null) => (s ? new Date(s).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '')

export const DealOnboarding: React.FC<{ dealId?: string; onboardingId?: string; dealName: string }> = ({ dealId, onboardingId, dealName }) => {
  const [cfg, setCfg] = useState<ObConfig | null>(null)
  const [ob, setOb] = useState<Onboarding | null>(null)
  const [missing, setMissing] = useState(false)
  const [users, setUsers] = useState<any[]>([])
  const [link, setLink] = useState<string | null>(null)
  const [sub, setSub] = useState<'checkpoints' | 'sessions'>('checkpoints')
  // SESSIONS timeline (Lee, 12 Oct 2026): meetings oldest→latest, latest open.
  const [sessions, setSessions] = useState<{ sessions: ObSession[]; otherPending: ObSessionTask[] } | null>(null)
  const [openSession, setOpenSession] = useState<string | null>(null)
  const loadSessions = (obIdNow: string, focusLatest = false) =>
    obSessions(obIdNow).then((r) => {
      setSessions(r)
      if (focusLatest) setOpenSession(r.sessions.length ? r.sessions[r.sessions.length - 1].id : null)
    }).catch(() => setSessions({ sessions: [], otherPending: [] }))
  const [openStage, setOpenStage] = useState<number | null>(null)
  const [goldPrice, setGoldPrice] = useState('')
  // GOLD QUOTE (Lee, 12 Oct 2026): price + custom payment link, settable
  // EVERY time a Gold quote goes out — assign card and the plan-bar button.
  const [goldLink, setGoldLink] = useState('')
  const [goldQuoteOpen, setGoldQuoteOpen] = useState(false)
  const [gqPrice, setGqPrice] = useState('')
  const [gqLink, setGqLink] = useState('')
  const [booking, setBooking] = useState<{ stageIndex: number | null } | null>(null)
  const [transcript, setTranscript] = useState<{ subject: string | null; ref?: string | null } | null>(null)
  const [transcriptText, setTranscriptText] = useState<string | null>(null)
  useEffect(() => {
    setTranscriptText(null)
    if (transcript?.ref) {
      fetch(transcript.ref).then((r) => (r.ok ? r.text() : Promise.reject())).then((t) => setTranscriptText(t.slice(0, 20000))).catch(() => setTranscriptText(null))
    }
  }, [transcript?.ref])
  const [summaryDraft, setSummaryDraft] = useState<Record<string, string>>({})
  const [blockersDraft, setBlockersDraft] = useState<Record<string, string>>({})
  const [nextActDraft, setNextActDraft] = useState<Record<string, string>>({})
  const [openMeeting, setOpenMeeting] = useState<string | null>(null)
  const [linkOpen, setLinkOpen] = useState(false)
  const [riskOpen, setRiskOpen] = useState(false)
  // ALL Book buttons open Calendly directly in a new tab (Lee, 2 Oct 2026).
  // The internal form stays reachable only via the small "log manually" link
  // (it creates the CRM meeting records that drive the cards + hours).
  const CALENDLY_URL = 'https://calendly.com/wequote/onboarding-training-call'
  const openCalendly = () => window.open(CALENDLY_URL, '_blank', 'noopener')
  // Plan upgrades + hours top-ups (Lee, 2 Oct 2026).
  const [upgradeOpen, setUpgradeOpen] = useState(false)
  const [upgradeGoldHours, setUpgradeGoldHours] = useState('')
  // "+ Add task" draft per stage (Lee, 1 Oct 2026): text + the
  // requires-upload-to-finish toggle from his v2 answer 4.
  const [taskDraft, setTaskDraft] = useState<{ si: number; text: string; ru: boolean; url: string; urlKind: 'page' | 'tutorial' } | null>(null)
  // Attached files/links (Lee, 2 Oct 2026): rendered as pills under their
  // step / custom task; refreshed with every reload.
  const [files, setFiles] = useState<any[]>([])
  // Staff attach control (Lee, 7 Oct 2026): file OR link, on upload steps and
  // custom tasks — mirrors the customer side.
  const [attach, setAttach] = useState<{ si: number; pi: number | null; ci: number | null; mode: 'file' | 'link'; name: string; url: string; busy: boolean } | null>(null)
  const attachDone = () => { setAttach(null); crmToast('Attached', 'success'); reload() }
  const attachFail = (e: any) => { setAttach((a) => (a ? { ...a, busy: false } : a)); crmToast(e?.message || 'Could not attach', 'error') }
  const pickFile = (file: File) => {
    if (!attach || !ob) return
    if (file.size > 15 * 1024 * 1024) { crmToast('File too large — 15 MB max', 'error'); return }
    setAttach({ ...attach, busy: true })
    const rd = new FileReader()
    rd.onload = () => obUploadFile(ob.id, { stageIndex: attach.si, stepIndex: attach.pi, customIndex: attach.ci, name: file.name, dataBase64: String(rd.result).split(',').pop() || '', mime: file.type || 'application/octet-stream' }).then(attachDone).catch(attachFail)
    rd.readAsDataURL(file)
  }
  const addLink = () => {
    if (!attach || !ob || !/^https:\/\/\S+$/.test(attach.url.trim())) return
    setAttach({ ...attach, busy: true })
    obAttachLink(ob.id, { stageIndex: attach.si, stepIndex: attach.pi, customIndex: attach.ci, name: attach.name.trim() || attach.url.trim(), url: attach.url.trim() }).then(attachDone).catch(attachFail)
  }
  const renderAttachBox = () => attach && (
    <div className="ml-6 mt-1 rounded-lg border border-brand-200 bg-brand-50/40 p-2 flex items-center gap-2 flex-wrap">
      <span className="inline-flex rounded-lg border border-border overflow-hidden">
        {(['file', 'link'] as const).map((m) => (
          <button key={m} onClick={() => setAttach({ ...attach, mode: m })} className={`px-2.5 py-1 text-[10px] font-bold ${attach.mode === m ? 'bg-txt-primary text-surface' : 'bg-surface text-txt-tertiary'}`}>{m === 'file' ? 'Upload file' : 'Add link'}</button>
        ))}
      </span>
      {attach.mode === 'file' ? (
        <label className={`px-3 py-1.5 rounded-lg text-[11px] font-bold text-white bg-brand-600 cursor-pointer ${attach.busy ? 'opacity-60' : 'hover:bg-brand-700'}`}>
          {attach.busy ? 'Uploading…' : 'Choose file (15 MB max)'}
          <input type="file" className="hidden" disabled={attach.busy} onChange={(e) => { const f = e.target.files?.[0]; if (f) pickFile(f) }} />
        </label>
      ) : (
        <>
          <input value={attach.name} onChange={(e) => setAttach({ ...attach, name: e.target.value })} placeholder="Title" className={`${inputCls} w-28`} />
          <input value={attach.url} onChange={(e) => setAttach({ ...attach, url: e.target.value })} placeholder="https://…" className={`${inputCls} flex-1 min-w-[160px]`} />
          <Button size="sm" disabled={attach.busy || !/^https:\/\/\S+$/.test(attach.url.trim())} onClick={addLink}>{attach.busy ? 'Adding…' : 'Add link'}</Button>
        </>
      )}
      <button onClick={() => setAttach(null)} className="text-[11px] font-semibold text-txt-tertiary hover:text-txt-primary">Cancel</button>
    </div>
  )
  const editTutorial = (si: number, pi: number, current: string | null) => {
    if (!ob) return
    const url = window.prompt('Tutorial link for THIS customer on this step (https://…).\nLeave empty to remove it.', current || '')
    if (url === null) return
    obSetStepTutorial(ob.id, { stageIndex: si, stepIndex: pi, url: url.trim() ? url.trim() : null })
      .then(() => { crmToast(url.trim() ? 'Tutorial added — "See tutorial" now shows on their page' : 'Tutorial removed', 'success'); reload() })
      .catch((e: any) => crmToast(e?.message || 'Not saved (https:// links only)', 'error'))
  }

  const reload = () =>
    (onboardingId ? getOnboarding(onboardingId) : obByLead(dealId!))
      .then((o) => {
        setOb(o); setMissing(false); if (openStage === null) setOpenStage(o.currentStage)
        obFiles(o.id).then(setFiles).catch(() => {})
      })
      .catch((e: any) => { if (e?.status === 404) setMissing(true); else crmToast('Could not load the onboarding', 'error') })
  useEffect(() => {
    getObConfig().then(setCfg).catch(() => {})
    getUsers().then(setUsers).catch(() => {})
    reload()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealId, onboardingId])
  useEffect(() => {
    if (ob?.id) obGetLink(ob.id).then((l) => setLink(l.url)).catch(() => setLink(null))
  }, [ob?.id])

  const copy = (text: string, what: string) => navigator.clipboard?.writeText(text).then(() => crmToast(`${what} copied`, 'success'))
  const embedCode = link
    ? `<iframe\n  src="${link}"\n  title="Onboarding"\n  style="width:100%;min-height:900px;border:0;"\n  allow="clipboard-write"\n  loading="lazy">\n</iframe>`
    : ''

  const stageMeetings = useMemo(() => {
    const m = new Map<number, NonNullable<Onboarding['meetings']>>()
    ;(ob?.meetings || []).forEach((mt) => {
      const k = mt.stageIndex ?? -1
      const a = m.get(k) || []
      a.push(mt)
      m.set(k, a)
    })
    return m
  }, [ob])

  const stageHours = (si: number) =>
    Math.round(((stageMeetings.get(si) || []).filter((m) => m.status === 'held').reduce((a, m) => a + (m.durationMin || 0), 0) / 60) * 10) / 10

  if (missing) {
    return (
      <div className="rounded-xl border border-dashed border-border bg-surface p-8 text-center max-w-md mx-auto mt-6">
        <p className="text-sm font-semibold text-txt-primary">No onboarding yet</p>
        <p className="text-[12px] text-txt-secondary mt-1">Start one to generate the customer link and checkpoints.</p>
        <Button size="sm" className="mt-3" disabled={!dealId} onClick={() => dealId && obStart(dealName, dealId).then(() => { crmToast('Onboarding started', 'success'); reload() }).catch(() => crmToast('Could not start', 'error'))}>
          Start onboarding
        </Button>
      </div>
    )
  }
  if (!ob || !cfg) return <SkeletonRows rows={5} rowClass="h-11" className="p-2" />

  const plan = ob.plan ? cfg.plans[ob.plan] : null
  const remaining = ob.hoursAllowance != null ? Math.max(0, ob.hoursAllowance - ob.hoursUsed) : null
  const overHours = !!ob.hoursAllowance && ob.hoursUsed > ob.hoursAllowance
  const pct = ob.hoursAllowance ? Math.min(100, (ob.hoursUsed / ob.hoursAllowance) * 100) : 0
  const barColor = overHours ? '#dc2626' : pct >= 80 ? '#f59e0b' : '#2563eb'
  // Upgrade targets: strictly higher plans only (Free→B/S/G, Bronze→S/G, Silver→G).
  const PLAN_ORDER = ['Free', 'Bronze', 'Silver', 'Gold']
  const upgradeTargets = ob.plan ? PLAN_ORDER.slice(PLAN_ORDER.indexOf(ob.plan) + 1) : []
  const inputCls = 'rounded-lg border border-border bg-surface px-2 py-1.5 text-[12px] text-txt-primary focus:outline-none focus:border-brand-400'

  return (
    <div className="space-y-4">
      {/* AGREED GO-LIVE DATE (Lee, 28 Oct 2026): set by US, prominent at the
          top; the customer sees it read-only as a banner on their page. */}
      <div className="rounded-xl border border-brand-200 bg-brand-50 px-4 py-3 flex items-center gap-3 flex-wrap shadow-sm">
        <div className="flex-1 min-w-[200px]">
          <p className="text-[10px] font-extrabold uppercase tracking-wider text-brand-700">Agreed go-live date</p>
          <p className={`text-[16px] font-bold ${ob.goLiveDate ? 'text-txt-primary' : 'text-txt-tertiary'}`}>
            {ob.goLiveDate ? new Date(ob.goLiveDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : 'Not agreed yet'}
          </p>
        </div>
        <input
          type="date"
          value={ob.goLiveDate ? String(ob.goLiveDate).slice(0, 10) : ''}
          onChange={(e) => obUpdate(ob.id, { goLiveDate: e.target.value || null }).then(() => { crmToast(e.target.value ? 'Agreed go-live set — the customer sees it too' : 'Agreed go-live cleared', 'success'); reload() }).catch(() => crmToast('Not saved', 'error'))}
          className={`${inputCls} font-semibold`}
          title="Agreed go-live — shows on the board card and, read-only, on the customer's onboarding page"
        />
      </div>
      {/* AT RISK (Phase 5, staff-only): urgent banner / suggestion banner. */}
      {ob.risk?.status === 'Urgent' && (
        <div className="rounded-xl border border-red-300 bg-red-50 px-4 py-3 flex items-center gap-3 flex-wrap">
          <p className="text-[12px] font-bold text-red-700 flex-1 min-w-[240px]">
            URGENT ACTION REQUIRED: Book a meeting or call to review the account, understand blockers and agree next steps.
          </p>
          <Button size="sm" onClick={openCalendly}>Book call</Button>
          <Button size="sm" variant="ghost" onClick={() => setRiskOpen(true)}>Review risk</Button>
        </div>
      )}
      {!ob.risk && ob.suggestions.length > 0 && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-2.5 flex items-center gap-3 flex-wrap">
          <p className="text-[12px] font-semibold text-amber-800 flex-1 min-w-[240px]">
            Suggested At Risk: {ob.suggestions.join(' · ')}
          </p>
          <Button size="sm" onClick={() => setRiskOpen(true)}>Add to At Risk</Button>
          <Button size="sm" variant="ghost" onClick={() => obDismissSuggestion(ob.id).then(() => { crmToast('Suggestion dismissed', 'success'); reload() })}>Dismiss</Button>
        </div>
      )}

      {/* TEST record banner — restart lives HERE only (server-guarded too). */}
      {ob.stagingMeta?.test === true && (
        <div className="rounded-xl border border-purple-300 bg-purple-50 px-4 py-2 flex items-center gap-3 flex-wrap">
          <p className="text-[12px] font-bold text-purple-800 flex-1 min-w-[220px]">
            TEST ONBOARDING — play with everything freely; Restart wipes it back to Not Started / awaiting plan.
          </p>
          <Button size="sm" variant="secondary" onClick={() => {
            if (!window.confirm('Restart this test onboarding? All ticks, meetings, plan and payment state are wiped.')) return
            obReset(ob.id).then(() => { crmToast('Restarted — back to Not Started', 'success'); setOpenStage(0); reload() }).catch((e: any) => crmToast(e?.message || 'Restart refused', 'error'))
          }}>↺ Restart onboarding</Button>
        </div>
      )}

      {/* PLAN BAR at the very top (Lee, 30 Sep 2026): plan chip · hours ·
          payment · risk · Customer link (popup) · Customer view · owner.
          The old customer-link row is condensed into the popup button. */}
      {/* PENDING HOURS REQUEST banner (Lee, 28 Oct 2026) — clears when topped up. */}
      {ob.hoursRequest && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-2.5 flex items-center gap-3 flex-wrap">
          <p className="text-[12px] font-bold text-amber-800 flex-1 min-w-[240px]">
            Customer requested{ob.hoursRequest.hours ? ` ${ob.hoursRequest.hours} extra hour${ob.hoursRequest.hours === 1 ? '' : 's'}` : ' more hours'}
            {ob.hoursRequest.note ? <span className="font-semibold"> — “{ob.hoursRequest.note}”</span> : null}
            <span className="font-medium text-amber-700"> · {new Date(ob.hoursRequest.at).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })}</span>
          </p>
          <Button size="sm" onClick={() => {
            const v = window.prompt('Add how many hours?', String(ob.hoursRequest?.hours || ''))
            if (v == null) return
            const hours = Number(v)
            if (!hours || hours <= 0) { crmToast('Enter a number of hours above 0', 'error'); return }
            obAddHours(ob.id, hours, 'customer request').then((r) => { crmToast(`Allowance now ${r.hoursAllowance}h — request cleared`, 'success'); reload() }).catch((e: any) => crmToast(e?.message || 'Top-up failed', 'error'))
          }}>Top up {ob.hoursRequest.hours ? `+${ob.hoursRequest.hours}h` : 'hours'}</Button>
        </div>
      )}
      {ob.planState === 'active' && (
        <div className="rounded-xl border border-border bg-surface p-3 flex items-center gap-3 flex-wrap shadow-sm">
          <span
            className="inline-flex items-center gap-1 h-8 px-3 rounded-lg text-[12px] font-bold bg-brand-50 text-brand-700 border border-brand-200 cursor-help"
            title={`Plan: ${ob.plan}\nIncluded: ${ob.hoursAllowance}h\nUsed: ${ob.hoursUsed}h\nRemaining: ${remaining}h\nAgreed go-live: ${ob.goLiveDate || '—'}`}
          >
            {ob.plan} ⓘ
          </span>
          <span className="flex items-center gap-2">
            {/* "4/4 hours complete + X over" (Lee, 5 Oct 2026) — no add-hours
                buttons any more; overage is chargeable via the notice banner. */}
            <span className={`text-[12px] font-semibold tabular-nums ${overHours ? 'text-red-600' : 'text-txt-primary'}`}>
              {overHours
                ? `${ob.hoursAllowance}/${ob.hoursAllowance} hours complete + ${Math.round((ob.hoursUsed - (ob.hoursAllowance || 0)) * 10) / 10} hours over`
                : `${ob.hoursUsed} of ${ob.hoursAllowance} hours`}
            </span>
            <span className="w-32 h-2 rounded-full bg-surface-tertiary overflow-hidden inline-block">
              <span className="block h-full rounded-full" style={{ width: `${pct}%`, background: barColor }} />
            </span>
            {ob.plan !== 'Gold' && (
              <button onClick={() => { setUpgradeGoldHours(''); setUpgradeOpen(true) }} className="px-2 py-1 rounded-lg text-[11px] font-bold bg-surface-tertiary text-txt-primary border border-border hover:bg-surface-hover" title={`Upgrade from ${ob.plan}`}>
                Upgrade ↑
              </button>
            )}
            {ob.plan === 'Gold' && (
              <button
                onClick={() => { setGqPrice(ob.price ? String(ob.price) : ''); setGqLink(''); setGoldQuoteOpen(true) }}
                className="px-2 py-1 rounded-lg text-[11px] font-bold bg-yellow-50 text-yellow-800 border border-yellow-300 hover:bg-yellow-100"
                title="Send a new Gold quote — set the price and a custom payment link; the customer's banner and Pay now button update immediately"
              >
                New Gold quote
              </button>
            )}
          </span>
          {(ob.paymentStatus === 'pending' || ob.paymentStatus === 'failed') && (
            <span className="flex items-center gap-1.5">
              <span className={`px-2 py-1 rounded-lg text-[11px] font-bold ${ob.paymentStatus === 'failed' ? 'bg-red-600 text-white border border-red-700' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                {ob.paymentStatus === 'failed' ? 'PAYMENT FAILED' : 'Payment pending'}{ob.price ? ` · £${ob.price.toLocaleString()}` : ''}
              </span>
              <Button size="sm" variant="secondary" onClick={() => (ob.paymentLinkUrl ? copy(ob.paymentLinkUrl, 'Payment link') : crmToast('No payment link on record yet', 'info'))}>Resend link</Button>
              <Button size="sm" variant="secondary" onClick={() => obMarkPaid(ob.id).then(() => { crmToast('Marked paid', 'success'); reload() })}>Mark paid</Button>
            </span>
          )}
          {ob.paymentStatus === 'processing' && (
            <span className="px-2 py-1 rounded-lg text-[11px] font-bold bg-amber-50 text-amber-700 border border-amber-300" title="Bank payment initiated via Stripe — confirms or fails automatically in a few days">
              Payment processing{ob.price ? ` · £${ob.price.toLocaleString()}` : ''}
            </span>
          )}
          {ob.paymentStatus === 'paid' && <span className="px-2 py-1 rounded-lg text-[11px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">Paid</span>}
          <button
            onClick={() => setRiskOpen(true)}
            className="px-2 py-1 rounded-lg text-[11px] font-bold text-white"
            style={{ background: ob.risk ? RISK_COLOR[ob.risk.status] || '#b45309' : '#94a3b8' }}
            title={ob.risk ? `${ob.risk.status} — ${(ob.risk.reasons || []).join(', ') || 'no reasons'}` : 'Open the At Risk panel'}
          >
            {ob.risk ? ob.risk.status.toUpperCase() : 'AT RISK?'}
          </button>
          <span className="ml-auto flex items-center gap-2">
            <Button size="sm" variant="secondary" onClick={() => setLinkOpen(true)}>Customer link</Button>
            <Button size="sm" variant="secondary" onClick={() => {
              const v = window.prompt('Top up hours — how many hours to ADD to the allowance?')
              if (v == null) return
              const hours = Number(v)
              if (!hours || hours <= 0) { crmToast('Enter a number of hours above 0', 'error'); return }
              const reason = window.prompt('Reason (optional — e.g. "customer request", "goodwill")') || undefined
              obAddHours(ob.id, hours, reason).then((r) => { crmToast(`Allowance now ${r.hoursAllowance}h`, 'success'); reload() }).catch((e: any) => crmToast(e?.message || 'Top-up failed', 'error'))
            }}>+ Top up hours</Button>
            {link && <a href={link} target="_blank" rel="noreferrer" className="text-[12px] font-semibold text-brand-600 hover:underline whitespace-nowrap">Customer view →</a>}
            <select
              value={ob.ownerId || ''}
              onChange={(e) => obUpdate(ob.id, { ownerId: e.target.value || null }).then(() => { crmToast('Owner updated', 'success'); reload() })}
              className={inputCls}
              title="Onboarding owner"
            >
              <option value="">Owner: unassigned</option>
              {users.map((u) => <option key={u.id} value={String(u.id)}>{u.name}</option>)}
            </select>
          </span>
        </div>
      )}

      {/* OVER-HOURS — ACTIONABLE BANNER (Lee, 5 Oct 2026): usage beyond the
          plan is chargeable; this sends the customer their page banner. A
          payment button lands here in a later phase. */}
      {ob.planState === 'active' && overHours && (
        <div className="rounded-xl border border-red-300 bg-red-50 px-4 py-3 flex items-center gap-3 flex-wrap">
          <p className="text-[12px] font-bold text-red-700 flex-1 min-w-[260px]">
            {ob.companyName} has gone {Math.round((ob.hoursUsed - (ob.hoursAllowance || 0)) * 10) / 10} hours beyond the {ob.hoursAllowance}h included in {ob.plan} — the extra hours are chargeable.
            {ob.overNotice && <span className="font-semibold text-red-600"> Customer notified {fmtD(ob.overNotice.at)}.</span>}
          </p>
          <Button
            size="sm"
            onClick={() => {
              if (!window.confirm(`Send ${ob.companyName} the over-hours notice?\n\nA banner appears on their onboarding page saying they've gone beyond their included hours and the extra hours are chargeable.`)) return
              obSendOverNotice(ob.id).then(() => { crmToast('Over-hours notice sent — their page now shows the banner', 'success'); reload() }).catch((e: any) => crmToast(e?.message || 'Could not send', 'error'))
            }}
          >{ob.overNotice ? 'Send the notice again' : 'Notify the customer'}</Button>
        </div>
      )}

      {/* ABOUT THE BUSINESS (spec §5, staff side — Lee, 30 Sep 2026): the
          answers card at the top of the tab. Modules immediately lock or
          unlock WorkHub / Procurement (billing overwrites when staging
          knows). The amber pill carries the open wording question. */}
      <div className="rounded-xl border border-border bg-surface p-4 shadow-sm">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-[14px] font-bold text-txt-primary">About the business</p>
          <p className="text-[11px] text-txt-tertiary">Drives which stages apply and how we run the sessions.</p>
          <span
            className="ml-auto px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-50 text-amber-700 border border-amber-300 cursor-help"
            title="To confirm with Lee: the final wording of these questions, and whether plan selection sits on the same page."
          >1 to confirm</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-3">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-txt-tertiary mb-1.5">Modules</p>
            {([['wh', 'WorkHub'], ['pr', 'Procurement & Inventory']] as const).map(([k, label]) => (
              <label key={k} className="flex items-center gap-2 text-[12px] text-txt-primary py-0.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!ob.mods?.[k]}
                  onChange={(e) => {
                    // Same confirmed switch as the stage toggles (Lee, 5 Oct 2026) — bp preserved.
                    const next = e.target.checked
                    if (!window.confirm(`Switch ${next ? 'ON' : 'OFF'} ${label} for ${ob.companyName}?`)) { e.preventDefault(); reload(); return }
                    const mods = { wh: !!ob.mods?.wh, pr: !!ob.mods?.pr, bp: !!ob.mods?.bp, [k]: next }
                    obSetAbout(ob.id, { mods }).then((r: any) => { (r.notices || []).forEach((n: string) => crmToast(n, 'success')); reload() }).catch(() => crmToast('Could not save', 'error'))
                  }}
                  className="w-4 h-4 accent-brand-600"
                />
                {label}
              </label>
            ))}
          </div>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-txt-tertiary mb-1.5">What is your accounting process?</p>
            <select
              value={ob.biz?.inv || ''}
              onChange={(e) => obSetAbout(ob.id, { biz: { inv: e.target.value || null } }).then(() => { crmToast('Saved', 'success'); reload() })}
              className={`${inputCls} w-full`}
            >
              <option value="">Not answered yet</option>
              <option>Take a deposit, then invoice by delivered</option>
              <option>Invoice and take payment</option>
            </select>
          </div>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-txt-tertiary mb-1.5">Team size</p>
            {/* Caption UNDER each input (Lee, 8 Oct 2026): the placeholder
                labels vanished once a number was typed; below the field keeps
                the 1 Oct top-alignment intact and the label always visible. */}
            <div className="flex gap-3">
              {([['sales', 'Sales'], ['techs', 'Technicians']] as const).map(([k, label]) => (
                <div key={k}>
                  <input
                    type="number" min={0} max={999}
                    placeholder={label}
                    title={`${label} team size`}
                    aria-label={`${label} team size`}
                    defaultValue={(ob.biz as any)?.[k] ?? ''}
                    onBlur={(e) => obSetAbout(ob.id, { biz: { [k]: e.target.value === '' ? null : Number(e.target.value) } as any }).then(() => crmToast('Saved', 'success'))}
                    className={`${inputCls} block w-24`}
                  />
                  <p className="mt-1 text-[9.5px] font-bold uppercase tracking-wide text-txt-tertiary text-center">{label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {ob.planState !== 'active' ? (
        /* ── NO PLAN: status + four plan cards with staff actions ── */
        <div className="space-y-3">
          <div className={`rounded-lg px-3 py-2 text-[12px] font-semibold ${ob.planState === 'requested' ? 'bg-blue-50 text-blue-800 border border-blue-300' : ob.planState === 'gold_requested' ? 'bg-yellow-50 text-yellow-800 border border-yellow-300' : 'bg-amber-50 text-amber-800 border border-amber-200'}`}>
            <span className="flex items-center gap-2 flex-wrap">
              <span className="flex-1 min-w-[240px]">
                {ob.planState === 'requested'
                  ? <>ONBOARDING REQUIRED — the company went paid{ob.requestedAt ? ` (${fmtD(ob.requestedAt)} ${fmtT(ob.requestedAt)})` : ''}. Review the company, then assign a plan below (enter the agreed price for Gold) — the customer gets the assignment banner with the payment link.</>
                  : ob.planState === 'gold_requested' ? 'GOLD REQUESTED — enter the agreed price below and send the link.' : 'No plan yet — assign one here; when the company goes paid it will flag itself ONBOARDING REQUIRED automatically.'}
                {ob.discoveryCallAt && <span className="font-normal"> · Discovery call booked {fmtD(ob.discoveryCallAt)} {fmtT(ob.discoveryCallAt)}</span>}
              </span>
              <Button size="sm" variant="secondary" onClick={() => setLinkOpen(true)}>Customer link</Button>
              {link && <a href={link} target="_blank" rel="noreferrer" className="text-[12px] font-semibold text-brand-600 hover:underline whitespace-nowrap">Customer view →</a>}
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
            {Object.entries(cfg.plans).map(([name, p]) => (
              <div key={name} className={`rounded-xl border bg-surface p-4 flex flex-col ${name === 'Gold' && ob.planState === 'gold_requested' ? 'border-yellow-400 ring-1 ring-yellow-300' : 'border-border'}`}>
                <div className="flex items-baseline justify-between">
                  <p className="text-[15px] font-bold text-txt-primary">{name}</p>
                  <p className="text-[12px] font-bold text-txt-primary">{p.poa ? `POA · from £${p.gbp.toLocaleString()}` : p.gbp ? `£${p.gbp.toLocaleString()} / $${p.usd.toLocaleString()}` : 'Free'}</p>
                </div>
                <p className="text-[11px] text-txt-secondary mt-1">{p.blurb}</p>
                <ul className="mt-2 space-y-1 flex-1">
                  {p.items.map((it) => <li key={it} className="text-[11px] text-txt-tertiary flex gap-1.5"><span className="text-emerald-600">✓</span>{it}</li>)}
                </ul>
                <p className="text-[10px] text-txt-tertiary mt-2">Up to {p.hrs} hrs</p>
                {name === 'Gold' ? (
                  <div className="mt-2 space-y-1.5">
                    {/* Custom payment link PER QUOTE (Lee, 13 Oct 2026): Gold is
                        ALWAYS a custom Stripe link for the agreed amount —
                        required, no env/test fallback. */}
                    <input value={goldLink} onChange={(e) => setGoldLink(e.target.value)} placeholder="Stripe payment link for this quote (required, https://…)" className={`${inputCls} w-full`} />
                    <div className="flex items-center gap-1.5">
                      <input value={goldPrice} onChange={(e) => setGoldPrice(e.target.value)} placeholder="Agreed £" className={`${inputCls} w-24`} />
                      <Button size="sm" className="flex-1" onClick={() => {
                        const n = Number(goldPrice)
                        if (!n || n <= 0) { crmToast('Enter the agreed price first', 'error'); return }
                        const lk = goldLink.trim()
                        if (!/^https:\/\/\S+$/.test(lk)) { crmToast('Paste the Stripe payment link for this quote (https:// URL) — Gold always uses a custom link', 'error'); return }
                        obGoldPrice(ob.id, n, undefined, lk).then(() => { crmToast('Gold assigned — payment pending with your link', 'success'); reload() }).catch(() => crmToast('Failed', 'error'))
                      }}>Assign Gold</Button>
                    </div>
                  </div>
                ) : (
                  <Button size="sm" className="mt-2" onClick={() =>
                    obSetPlan(ob.id, { plan: name }).then(() => { crmToast(`${name} assigned${name === 'Free' ? '' : ' — payment pending'}`, 'success'); reload() }).catch(() => crmToast('Failed', 'error'))
                  }>{name === 'Free' ? 'Assign Free' : 'Assign & send payment link'}</Button>
                )}
              </div>
            ))}
          </div>
          <p className="text-[10px] text-txt-tertiary">{cfg.modulesNote}</p>
        </div>
      ) : (
        /* ── PLAN ACTIVE: Checkpoints|Bookings (the plan bar lives at the top). ── */
        <>
          {/* ONE meetings view (Lee, 12 Oct 2026): Bookings merged into Sessions —
              same records, single tab, no information split. */}
          <SegTabs size="sm" tabs={[{ key: 'checkpoints', label: 'Checkpoints' }, { key: 'sessions', label: 'Sessions' }]} value={sub} onChange={(k) => { setSub(k as any); if (k === 'sessions' && ob) loadSessions(ob.id, true) }} />

          {sub === 'checkpoints' && (
            <div className="space-y-2.5">
              {cfg.stages.map((st, si) => {
                const s = ob.stages[si]
                const complete = s.done >= s.total
                const isCurrent = si === ob.currentStage
                const firstFuture = !complete && !isCurrent && si === ob.stages.findIndex((x, i) => i > ob.currentStage && x.done < x.total)
                const meets = stageMeetings.get(si) || []
                const heldN = meets.filter((m) => m.status === 'held').length
                const upN = meets.filter((m) => m.status === 'upcoming').length
                // MODULE STAGES (Lee, 5 Oct 2026): WorkHub / Procurement /
                // Bespoke are fully greyed and unopenable until WE switch
                // them on — the toggle below, with a confirm either way.
                const modKey = st.module as 'wh' | 'pr' | 'bp' | undefined
                const modOn = modKey ? !!(ob.mods as any)?.[modKey] : true
                const switchedOff = !!modKey && !modOn
                const open = openStage === si && !switchedOff
                const toggleModule = (e: React.MouseEvent) => {
                  e.stopPropagation()
                  const next = !modOn
                  const msg = next
                    ? `Switch ON ${st.name} for ${ob.companyName}?\n\nThe stage opens on both sides (ticking + auto triggers) and counts toward completion.`
                    : `Switch OFF ${st.name} for ${ob.companyName}?\n\nThe stage greys out on both sides and stops counting. Ticks, files and meetings are kept.`
                  if (!window.confirm(msg)) return
                  obSetAbout(ob.id, { mods: { wh: !!ob.mods?.wh, pr: !!ob.mods?.pr, bp: !!ob.mods?.bp, [modKey!]: next } as any })
                    .then((r: any) => { (r.notices || []).forEach((n: string) => crmToast(n, 'success')); crmToast(`${st.name} switched ${next ? 'ON' : 'OFF'}`, 'success'); if (!next && openStage === si) setOpenStage(null); reload() })
                    .catch((err: any) => crmToast(err?.message || 'Could not switch', 'error'))
                }
                return (
                  <div key={st.name} className={`rounded-xl border bg-surface overflow-hidden ${switchedOff ? 'opacity-45 border-border' : isCurrent ? 'border-brand-300' : 'border-border'} ${!switchedOff && !complete && !isCurrent ? 'opacity-80' : ''}`}>
                    <button onClick={() => !switchedOff && setOpenStage(open ? null : si)} className={`w-full flex items-center gap-2.5 px-4 py-3 text-left ${switchedOff ? 'cursor-default' : 'hover:bg-surface-hover'}`} title={switchedOff ? 'Switched off — toggle it on to open this stage' : undefined}>
                      <span className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${!switchedOff && complete ? 'bg-emerald-500 text-white' : !switchedOff && isCurrent ? 'bg-brand-600 text-white' : 'bg-surface-tertiary text-txt-secondary'}`} title={st.name}>
                        {!switchedOff && complete ? '✓' : <ObStageIcon short={st.short} className="w-4 h-4" />}
                      </span>
                      <span className="text-[14px] font-bold text-txt-primary">{st.name}</span>
                      {switchedOff && <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-surface-tertiary text-txt-tertiary border border-border">SWITCHED OFF</span>}
                      {!switchedOff && isCurrent && <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-brand-50 text-brand-700 border border-brand-200">Current</span>}
                      {!switchedOff && firstFuture && <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-surface-tertiary text-txt-secondary border border-border">Up next</span>}
                      <span className="ml-auto flex items-center gap-2 shrink-0">
                        {!switchedOff && <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-surface-tertiary text-txt-secondary border border-border">{s.done}/{s.total} steps done</span>}
                        {!switchedOff && <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-surface-tertiary text-txt-secondary border border-border">{stageHours(si)} hrs</span>}
                        {modKey && (
                          <span
                            role="switch" aria-checked={modOn}
                            onClick={toggleModule}
                            className={`relative inline-flex w-9 h-5 rounded-full cursor-pointer transition-colors shrink-0 ${modOn ? 'bg-emerald-500' : 'bg-surface-tertiary border border-border'}`}
                            title={modOn ? `Switch ${st.name} off` : `Switch ${st.name} on`}
                          >
                            <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${modOn ? 'left-[18px]' : 'left-0.5'}`} />
                          </span>
                        )}
                        {!switchedOff && <span className={`text-txt-tertiary transition-transform ${open ? 'rotate-90' : ''}`}>›</span>}
                      </span>
                    </button>
                    {open && (
                      <div className="border-t border-border flex flex-col lg:flex-row">
                        {/* Checklist ~65% */}
                        <div className="flex-1 lg:basis-[65%] p-3 space-y-0.5">
                          {st.steps.map((sx, pi) => {
                            const step = (ob.steps || []).find((x) => x.stageIndex === si && x.stepIndex === pi)
                            const done = !!step?.done
                            const TAG_CLS: Record<string, string> = {
                              AUTO: 'bg-emerald-50 text-emerald-700 border-emerald-200',
                              MANUAL: 'bg-surface-tertiary text-txt-tertiary border-border',
                              CALL: 'bg-violet-50 text-violet-700 border-violet-200',
                              BOOK: 'bg-rose-50 text-rose-700 border-rose-200',
                            }
                            return (
                              <div key={pi} className="px-1.5 py-1 rounded-lg hover:bg-surface-hover group">
                                <div className="flex items-center gap-2">
                                  <input
                                    type="checkbox"
                                    checked={done}
                                    disabled={!!sx.counter}
                                    onChange={(e) => obSetStep(ob.id, si, pi, e.target.checked).then((r: any) => { reload(); (r.notices || []).forEach((n: string) => crmToast(n, 'success')); if (r.movedTo === 'complete') crmToast('All steps done — the card moves to Complete', 'success') }).catch((err: any) => crmToast(err?.message || 'Not allowed', 'error'))}
                                    className="w-4 h-4 accent-emerald-600 cursor-pointer disabled:cursor-not-allowed"
                                  />
                                  <span className={`text-[12px] flex-1 ${done ? 'text-txt-tertiary line-through decoration-txt-tertiary/50' : 'text-txt-primary'}`} title={done && step?.doneAt ? `Done ${fmtD(step.doneAt)}${step.doneBy ? ` by ${step.doneBy}` : ''}` : undefined}>
                                    {sx.t}{sx.optional && <span className="text-txt-tertiary"> · optional</span>}
                                  </span>
                                  <span className={`px-1.5 py-0.5 rounded text-[8px] font-bold border ${TAG_CLS[sx.tag] || TAG_CLS.MANUAL}`}>{sx.tag}</span>
                                  {sx.tn && <span className="px-1.5 py-0.5 rounded text-[8px] font-bold bg-amber-50 text-amber-700 border border-amber-200">No tutorial yet</span>}
                                  {/* Per-step action (Lee, 1 Oct 2026): the ↗ now goes to the
                                      step's OWN page in the company's account (alias-resolved
                                      server-side), tutorials get a Watch tutorial button, BOOK
                                      steps a Book button. No link → no action. */}
                                  {step?.link?.kind === 'page' && step.link.url && (
                                    <a href={step.link.url} target="_blank" rel="noreferrer" className="text-[10px] font-semibold text-txt-tertiary group-hover:text-brand-600 whitespace-nowrap no-underline">Go to page ↗</a>
                                  )}
                                  {step?.link?.kind === 'tutorial' && step.link.url && (
                                    <a href={step.link.url} target="_blank" rel="noreferrer" className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-brand-50 text-brand-700 border border-brand-200 hover:bg-brand-100 whitespace-nowrap no-underline">▶ Watch tutorial</a>
                                  )}
                                  {step?.link?.kind === 'book' && (
                                    <a href={CALENDLY_URL} target="_blank" rel="noreferrer" className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-rose-50 text-rose-700 border border-rose-200 hover:bg-rose-100 whitespace-nowrap no-underline">Book meeting</a>
                                  )}
                                  {/* Per-customer tutorial (Lee, 7 Oct 2026). */}
                                  {step?.tutorialUrl && (
                                    <a href={step.tutorialUrl} target="_blank" rel="noreferrer" className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-violet-50 text-violet-700 border border-violet-200 hover:bg-violet-100 whitespace-nowrap no-underline" title="Tutorial attached for THIS customer">▶ See tutorial</a>
                                  )}
                                  <button
                                    onClick={() => editTutorial(si, pi, step?.tutorialUrl || null)}
                                    className={`text-[9px] font-bold whitespace-nowrap ${step?.tutorialUrl ? 'text-violet-600 hover:underline' : 'text-txt-tertiary opacity-0 group-hover:opacity-100 hover:text-brand-600'}`}
                                    title="Attach a tutorial link to this step — this customer only"
                                  >{step?.tutorialUrl ? '✎' : '+ tutorial'}</button>
                                </div>
                                {sx.counter && (
                                  <div className="ml-6 mt-0.5 flex items-center gap-1.5">
                                    {Array.from({ length: sx.counter }, (_, k) => (
                                      <span key={k} className={`w-2 h-2 rounded-full ${k < (step?.n || 0) ? 'bg-emerald-500' : 'bg-surface-tertiary border border-border'}`} />
                                    ))}
                                    <span className="text-[10px] text-txt-tertiary">{step?.n || 0}/{sx.counter}</span>
                                    <button onClick={() => obSetCounter(ob.id, si, pi, (step?.n || 0) + 1).then((r: any) => { reload(); (r.notices || []).forEach((n: string) => crmToast(n, 'success')) })} className="text-[10px] font-bold text-brand-600 hover:underline">+1 (interim)</button>
                                  </div>
                                )}
                                {sx.subs && (
                                  <div className="ml-6 mt-1 rounded-lg bg-surface-secondary/70 border border-border p-2 space-y-0.5">
                                    {sx.subLabel && <p className="text-[10px] font-bold text-txt-tertiary uppercase tracking-wide">{sx.subLabel}</p>}
                                    {sx.subs.map((sub, k) => (
                                      <label key={k} className="flex items-center gap-1.5 text-[11px] text-txt-secondary cursor-pointer">
                                        <input type="checkbox" checked={!!step?.subs?.[k]} onChange={(e) => obSetSub(ob.id, si, pi, k, e.target.checked).then((r: any) => { reload(); (r.notices || []).forEach((n: string) => crmToast(n, 'success')) })} className="w-3.5 h-3.5 accent-emerald-600" />
                                        {sub}
                                      </label>
                                    ))}
                                  </div>
                                )}
                                {sx.note && <p className="ml-6 text-[10px] text-txt-tertiary mt-0.5">{sx.note}</p>}
                                {files.some((f) => f.stageIndex === si && f.stepIndex === pi) && (
                                  <div className="ml-6 mt-1 flex items-center gap-1.5 flex-wrap">
                                    {files.filter((f) => f.stageIndex === si && f.stepIndex === pi).map((f) => <FilePill key={f.id} f={f} />)}
                                  </div>
                                )}
                                {/* Attach on upload steps (Lee, 7 Oct 2026) — staff side. */}
                                {isUploadStep(sx, si, pi) && (
                                  attach && attach.si === si && attach.pi === pi && attach.ci === null
                                    ? renderAttachBox()
                                    : <button onClick={() => setAttach({ si, pi, ci: null, mode: 'file', name: '', url: '', busy: false })} className="ml-6 mt-0.5 text-[10px] font-bold text-brand-600 hover:underline opacity-0 group-hover:opacity-100">+ Add file / link</button>
                                )}
                              </div>
                            )
                          })}
                          {/* ── Custom tasks (Lee, 1 Oct 2026): staff-added, per stage.
                              requiresUpload tasks refuse to complete until a file is
                              attached (server-enforced). ── */}
                          {(ob.custom?.[si] || []).length > 0 && (
                            <div className="mt-1.5 pt-1.5 border-t border-dashed border-border space-y-0.5">
                              {(ob.custom?.[si] || []).map((t, ci) => (
                                <div key={ci} className="px-1.5 py-1 rounded-lg hover:bg-surface-hover group">
                                <div className="flex items-center gap-2">
                                  <input
                                    type="checkbox"
                                    checked={!!t.done}
                                    onChange={() => obCustomTask(ob.id, { stageIndex: si, action: 'toggle', index: ci }).then((r: any) => { if (r.ok === false) { crmToast(r.error || 'Not allowed', 'error'); return } reload(); (r.notices || []).forEach((n: string) => crmToast(n, 'success')) }).catch((err: any) => crmToast(err?.message || 'Not allowed', 'error'))}
                                    className="w-4 h-4 accent-emerald-600 cursor-pointer"
                                  />
                                  <span className={`text-[12px] flex-1 ${t.done ? 'text-txt-tertiary line-through decoration-txt-tertiary/50' : 'text-txt-primary'}`}>{t.t}</span>
                                  {t.requiresUpload && <span className="px-1.5 py-0.5 rounded text-[8px] font-bold bg-blue-50 text-blue-700 border border-blue-200" title="Needs a file uploaded before it can be completed">NEEDS UPLOAD</span>}
                                  <span className="px-1.5 py-0.5 rounded text-[8px] font-bold bg-indigo-50 text-indigo-700 border border-indigo-200">CUSTOM</span>
                                  {t.url && t.urlKind === 'tutorial' && <a href={t.url} target="_blank" rel="noreferrer" className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-brand-50 text-brand-700 border border-brand-200 hover:bg-brand-100 whitespace-nowrap no-underline">▶ Watch tutorial</a>}
                                  {t.url && t.urlKind !== 'tutorial' && <a href={t.url} target="_blank" rel="noreferrer" className="text-[10px] font-semibold text-txt-tertiary group-hover:text-brand-600 whitespace-nowrap no-underline">Go to page ↗</a>}
                                  <button
                                    onClick={() => { if (window.confirm(`Remove task "${t.t}"?`)) obCustomTask(ob.id, { stageIndex: si, action: 'remove', index: ci }).then(() => reload()) }}
                                    className="text-[12px] text-txt-tertiary opacity-0 group-hover:opacity-100 hover:text-red-600 font-bold"
                                    title="Remove task"
                                  >×</button>
                                </div>
                                {files.some((f) => f.stageIndex === si && f.customIndex === ci) && (
                                  <div className="ml-6 mt-1 flex items-center gap-1.5 flex-wrap">
                                    {files.filter((f) => f.stageIndex === si && f.customIndex === ci).map((f) => <FilePill key={f.id} f={f} />)}
                                  </div>
                                )}
                                {attach && attach.si === si && attach.ci === ci && attach.pi === null
                                  ? renderAttachBox()
                                  : <button onClick={() => setAttach({ si, pi: null, ci, mode: 'file', name: '', url: '', busy: false })} className="ml-6 mt-0.5 text-[10px] font-bold text-brand-600 hover:underline opacity-0 group-hover:opacity-100">+ Add file / link</button>}
                                </div>
                              ))}
                            </div>
                          )}
                          {taskDraft?.si === si ? (
                            <div className="mt-1.5 px-1.5 py-1.5 rounded-lg border border-brand-200 bg-brand-50/40 space-y-1.5">
                              <input
                                autoFocus
                                value={taskDraft.text}
                                onChange={(e) => setTaskDraft({ ...taskDraft, text: e.target.value })}
                                onKeyDown={(e) => { if (e.key === 'Escape') setTaskDraft(null) }}
                                placeholder="What needs doing?"
                                className="w-full rounded-lg border border-border bg-surface px-2.5 py-1.5 text-[12px] text-txt-primary outline-none focus:border-brand-400"
                              />
                              <div className="flex items-center gap-1.5">
                                <input
                                  value={taskDraft.url}
                                  onChange={(e) => setTaskDraft({ ...taskDraft, url: e.target.value })}
                                  onKeyDown={(e) => { if (e.key === 'Escape') setTaskDraft(null) }}
                                  placeholder="Link URL (optional) — use {alias} for the company's alias"
                                  className="flex-1 rounded-lg border border-border bg-surface px-2.5 py-1.5 text-[11px] text-txt-primary outline-none focus:border-brand-400"
                                />
                                {/* What the link IS (Lee, 2 Oct 2026): page vs tutorial. */}
                                <select
                                  value={taskDraft.urlKind}
                                  onChange={(e) => setTaskDraft({ ...taskDraft, urlKind: e.target.value as 'page' | 'tutorial' })}
                                  className="rounded-lg border border-border bg-surface px-2 py-1.5 text-[11px] text-txt-primary outline-none"
                                  title="What this link is — shows as 'Go to page ↗' or '▶ Watch tutorial'"
                                >
                                  <option value="page">Page</option>
                                  <option value="tutorial">Tutorial</option>
                                </select>
                              </div>
                              <div className="flex items-center gap-2.5 flex-wrap">
                                <label className="flex items-center gap-1.5 text-[11px] text-txt-secondary cursor-pointer">
                                  <input type="checkbox" checked={taskDraft.ru} onChange={(e) => setTaskDraft({ ...taskDraft, ru: e.target.checked })} className="w-3.5 h-3.5 accent-brand-600" />
                                  Requires an upload to finish
                                </label>
                                <span className="ml-auto flex items-center gap-1.5">
                                  <Button
                                    size="sm"
                                    disabled={!taskDraft.text.trim()}
                                    onClick={() => obCustomTask(ob.id, { stageIndex: si, action: 'add', text: taskDraft.text.trim(), requiresUpload: taskDraft.ru, ...(taskDraft.url.trim() ? { url: taskDraft.url.trim(), urlKind: taskDraft.urlKind } : {}) }).then((r: any) => { if (r.ok === false) { crmToast(r.error || 'Could not add', 'error'); return } setTaskDraft(null); reload(); crmToast('Task added', 'success') }).catch((err: any) => crmToast(err?.message || 'Could not add', 'error'))}
                                  >Add task</Button>
                                  <button onClick={() => setTaskDraft(null)} className="text-[11px] font-semibold text-txt-tertiary hover:text-txt-primary">Cancel</button>
                                </span>
                              </div>
                            </div>
                          ) : (
                            <button onClick={() => setTaskDraft({ si, text: '', ru: false, url: '', urlKind: 'page' })} className="mt-1 px-1.5 py-1 text-[11px] font-bold text-brand-600 hover:underline">+ Add task</button>
                          )}
                        </div>
                        {/* Meetings ~35% */}
                        <div className="lg:basis-[35%] border-t lg:border-t-0 lg:border-l border-border p-3">
                          <p className="text-[12px] font-bold text-txt-primary">{heldN} meeting{heldN === 1 ? '' : 's'} done{upN ? ` · ${upN} upcoming` : ''}</p>
                          <div className="mt-1.5 space-y-1">
                            {meets.slice(0, 5).map((m) => (
                              <div key={m.id} className="flex items-center gap-2 text-[11px]">
                                <span className="tabular-nums font-semibold text-txt-primary">{fmtD(m.startAt)}</span>
                                {m.status === 'upcoming' && <span className="px-1.5 py-0.5 rounded text-[8px] font-bold bg-blue-50 text-blue-700 border border-blue-200">Upcoming</span>}
                                <button onClick={() => { setSub('sessions'); if (ob) loadSessions(ob.id, true) }} className="ml-auto text-brand-600 font-semibold hover:underline">See more</button>
                              </div>
                            ))}
                            {meets.length === 0 && <p className="text-[11px] text-txt-tertiary">No meetings for this stage yet.</p>}
                          </div>
                          <div className="mt-2.5 flex items-center gap-2.5">
                            <Button size="sm" onClick={openCalendly}>Book Meeting</Button>
                            <button onClick={() => setBooking({ stageIndex: si })} className="text-[10px] font-semibold text-txt-tertiary hover:text-brand-600 hover:underline" title="Record a meeting in WeTask (drives the cards + hours)">log manually</button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}

          {/* ── SESSIONS (Lee, 12 Oct 2026): onboarding meetings as a timeline,
                oldest → latest (latest open) — per session: its tasks (tickable),
                Gemini notes, transcript. Side panel: every pending task and
                which session it came from. ── */}
          {sub === 'sessions' && (() => {
            const tickTask = (t: ObSessionTask) =>
              obCustomTask(ob.id, { stageIndex: t.stageIndex, action: 'toggle', index: t.index })
                .then(() => { loadSessions(ob.id); reload() })
                .catch((e: any) => crmToast(e?.message || 'Could not update the task', 'error'))
            const whoBooked = (s: ObSession) => s.bookedBy === 'calendar-mirror' ? 'Google Calendar' : s.bookedBy === 'customer' ? 'customer' : s.bookedBy?.startsWith('staff:') ? (users.find((u) => String(u.id) === s.bookedBy!.slice(6))?.name || 'staff') : (s.bookedBy || '—')
            const TaskRow: React.FC<{ t: ObSessionTask; showSrc?: string }> = ({ t, showSrc }) => (
              <label className="flex items-start gap-2 py-1 cursor-pointer group">
                <input type="checkbox" checked={t.done} onChange={() => tickTask(t)} className="mt-0.5 w-3.5 h-3.5 accent-emerald-600 shrink-0" />
                <span className="min-w-0">
                  <span className={`text-[12px] leading-snug ${t.done ? 'line-through text-txt-tertiary' : 'text-txt-primary'}`}>{t.text}</span>
                  {showSrc && <span className="block text-[9px] font-semibold text-txt-tertiary">{showSrc}</span>}
                </span>
              </label>
            )
            if (!sessions) return <p className="text-[12px] text-txt-tertiary">Loading sessions…</p>
            const pendingBySession = sessions.sessions.map((s) => ({ s, pending: s.tasks.filter((t) => !t.done) })).filter((x) => x.pending.length)
            return (
              <div className="flex gap-4 items-start">
                {/* Timeline frame */}
                <div className="flex-1 min-w-0">
                  {/* Booking entry points live here now (Bookings tab merged in). */}
                  <div className="flex items-center justify-end gap-2.5 mb-2">
                    <Button size="sm" onClick={openCalendly}>Book Meeting</Button>
                    <button onClick={() => setBooking({ stageIndex: ob.currentStage })} className="text-[10px] font-semibold text-txt-tertiary hover:text-brand-600 hover:underline" title="Record a meeting in WeTask (drives the cards + hours)">log manually</button>
                  </div>
                  {sessions.sessions.length === 0 ? (
                    <div className="rounded-xl border border-dashed border-border p-6 text-center text-[12px] text-txt-tertiary">
                      No onboarding sessions yet — meetings with “onboarding” or “training” in the title land here automatically (15-min sync), or log one manually above.
                    </div>
                  ) : (
                    <div className="relative pl-5">
                      <div className="absolute left-[5px] top-2 bottom-2 w-px bg-border" />
                      {sessions.sessions.map((s, i) => {
                        const open = openSession === s.id
                        const latest = i === sessions.sessions.length - 1
                        return (
                          <div key={s.id} className="relative pb-2.5">
                            <span className={`absolute -left-[15px] top-2.5 w-[11px] h-[11px] rounded-full border-2 border-surface ${s.status === 'held' ? 'bg-emerald-500' : s.status === 'upcoming' ? 'bg-brand-600' : 'bg-slate-400'}`} />
                            <div className={`rounded-xl border bg-surface overflow-hidden ${open ? 'border-brand-300 shadow-sm' : 'border-border'}`}>
                              <button onClick={() => setOpenSession(open ? null : s.id)} className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-left hover:bg-surface-hover">
                                <span className="min-w-0 flex-1">
                                  <span className="text-[13px] font-bold text-txt-primary block truncate">
                                    {s.subject || 'Onboarding session'}
                                    {s.stageIndex != null && cfg.stages[s.stageIndex] && <span className="ml-2 px-1.5 py-0.5 rounded text-[9px] font-bold bg-surface-tertiary text-txt-secondary border border-border align-middle">{cfg.stages[s.stageIndex].name}</span>}
                                  </span>
                                  <span className="text-[11px] text-txt-secondary">{fmtD(s.startAt)} {fmtT(s.startAt)}{s.durationMin ? ` · ${s.durationMin} min` : ''} · via {whoBooked(s)}{s.attendees.length ? ` · ${s.attendees.map((a) => a.split('@')[0]).slice(0, 3).join(', ')}` : ''}</span>
                                </span>
                                {latest && <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-brand-50 text-brand-700 border border-brand-200 shrink-0">LATEST</span>}
                                <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase shrink-0 ${s.status === 'held' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : s.status === 'upcoming' ? 'bg-blue-50 text-blue-700 border border-blue-200' : 'bg-surface-tertiary text-txt-secondary border border-border'}`}>{s.status}</span>
                                {s.tasks.length > 0 && <span className="text-[10px] font-bold text-txt-secondary tabular-nums shrink-0">{s.tasks.filter((t) => t.done).length}/{s.tasks.length} tasks</span>}
                              </button>
                              {open && s.status === 'upcoming' && (
                                <div className="px-3.5 py-3 border-t border-border flex items-center gap-2">
                                  <Button size="sm" variant="secondary" onClick={() => {
                                    const v = prompt('New date & time (YYYY-MM-DD HH:MM)', s.startAt ? s.startAt.slice(0, 16).replace('T', ' ') : '')
                                    if (!v) return
                                    const iso = new Date(v.replace(' ', 'T')).toISOString()
                                    obUpdateMeeting(ob.id, s.id, { startAt: iso }).then(() => { crmToast('Rescheduled', 'success'); loadSessions(ob.id); reload() }).catch(() => crmToast('Bad date', 'error'))
                                  }}>Reschedule</Button>
                                  <Button size="sm" variant="ghost" onClick={() => obUpdateMeeting(ob.id, s.id, { status: 'cancelled' }).then(() => { crmToast('Cancelled', 'success'); loadSessions(ob.id); reload() })}>Cancel</Button>
                                  <span className="ml-auto text-[11px] text-txt-tertiary">Flips to held automatically after it ends.</span>
                                </div>
                              )}
                              {open && s.status !== 'upcoming' && (
                                <div className="px-3.5 pb-3 pt-2 border-t border-border space-y-3">
                                  {/* Tasks from the session (Gemini "Suggested next steps") */}
                                  {s.tasks.length > 0 && (
                                    <div>
                                      <p className="text-[9px] font-bold uppercase tracking-wider text-txt-secondary mb-0.5">Tasks from this session</p>
                                      {s.tasks.map((t) => <TaskRow key={`${t.stageIndex}:${t.index}`} t={t} />)}
                                    </div>
                                  )}
                                  {/* Gemini notes per recorder + transcript */}
                                  {s.notes.map((n, ni) => (
                                    <details key={ni} className="rounded-lg border border-border bg-surface-secondary/50">
                                      <summary className="px-2.5 py-1.5 text-[11px] font-bold text-txt-primary cursor-pointer select-none">
                                        Summary & notes by {n.owner}{n.docUrl ? <a href={n.docUrl} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="ml-2 font-semibold text-brand-600 hover:underline">open doc ↗</a> : null}
                                      </summary>
                                      <pre className="px-2.5 pb-2.5 text-[11px] text-txt-secondary whitespace-pre-wrap font-sans max-h-72 overflow-y-auto">{n.notesText}</pre>
                                    </details>
                                  ))}
                                  {s.transcriptRef && (
                                    <a href={s.transcriptRef} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[11px] font-bold text-brand-600 hover:underline">
                                      Open transcript / recording ↗
                                    </a>
                                  )}
                                  {!s.tasks.length && !s.notes.length && !s.transcriptRef && (
                                    <p className="text-[11px] text-txt-tertiary">No Gemini notes for this session yet — they sync within ~15 minutes of appearing on the calendar event.</p>
                                  )}
                                  {/* Editing kit (merged from the old Bookings tab, Lee 12 Oct 2026) */}
                                  <div className="flex flex-col lg:flex-row gap-3">
                                    <div className="flex-1 min-w-0 space-y-2.5">
                                      <div>
                                        <p className="text-[10px] font-bold uppercase tracking-wider text-txt-tertiary mb-1">What's been done</p>
                                        <textarea
                                          value={summaryDraft[s.id] ?? s.summary ?? ''}
                                          onChange={(e) => setSummaryDraft((d) => ({ ...d, [s.id]: e.target.value }))}
                                          onBlur={() => { const v = summaryDraft[s.id]; if (v !== undefined && v !== (s.summary || '')) obUpdateMeeting(ob.id, s.id, { summary: v }).then(() => { crmToast('Saved', 'success'); loadSessions(ob.id) }) }}
                                          rows={3} placeholder="Notes on what was covered…"
                                          className="w-full rounded-lg border border-border bg-surface px-2.5 py-2 text-[12px] text-txt-primary focus:outline-none focus:border-brand-400 resize-y"
                                        />
                                      </div>
                                      <div>
                                        <p className="text-[10px] font-bold uppercase tracking-wider text-txt-tertiary mb-1">Blockers</p>
                                        <textarea
                                          value={blockersDraft[s.id] ?? s.blockers ?? ''}
                                          onChange={(e) => setBlockersDraft((d) => ({ ...d, [s.id]: e.target.value }))}
                                          onBlur={() => { const v = blockersDraft[s.id]; if (v !== undefined && v !== (s.blockers || '')) obMeetingNotes(ob.id, s.id, { blockers: v || null }).then(() => { crmToast('Saved', 'success'); loadSessions(ob.id); reload() }) }}
                                          rows={2} placeholder="Anything stopping progress"
                                          className="w-full rounded-lg border border-border bg-surface px-2.5 py-2 text-[12px] text-txt-primary focus:outline-none focus:border-brand-400 resize-y"
                                        />
                                      </div>
                                      <div>
                                        <p className="text-[10px] font-bold uppercase tracking-wider text-txt-tertiary mb-1">Next action <span className="normal-case font-medium text-txt-tertiary/80">drives health status</span></p>
                                        <input
                                          value={nextActDraft[s.id] ?? s.nextAct ?? ''}
                                          onChange={(e) => setNextActDraft((d) => ({ ...d, [s.id]: e.target.value }))}
                                          onBlur={() => { const v = nextActDraft[s.id]; if (v !== undefined && v !== (s.nextAct || '')) obMeetingNotes(ob.id, s.id, { nextAct: v || null }).then(() => { crmToast('Saved', 'success'); loadSessions(ob.id); reload() }) }}
                                          placeholder="The agreed next step…"
                                          className="w-full rounded-lg border border-border bg-surface px-2.5 py-2 text-[12px] text-txt-primary focus:outline-none focus:border-brand-400"
                                        />
                                        <div className="flex gap-2 mt-2">
                                          <select
                                            value={s.nextOwnerId || ''}
                                            onChange={(e) => obMeetingNotes(ob.id, s.id, { nextOwnerId: e.target.value || null }).then(() => { crmToast('Saved', 'success'); loadSessions(ob.id); reload() })}
                                            className={`${inputCls} flex-1`}
                                          >
                                            <option value="">Owner…</option>
                                            {users.map((u) => <option key={u.id} value={String(u.id)}>{u.name}</option>)}
                                          </select>
                                          <input
                                            type="date" value={s.nextDue || ''}
                                            onChange={(e) => obMeetingNotes(ob.id, s.id, { nextDue: e.target.value || null }).then(() => { crmToast('Saved', 'success'); loadSessions(ob.id); reload() })}
                                            className={inputCls}
                                          />
                                        </div>
                                      </div>
                                    </div>
                                    {s.stageIndex != null && obCoveredList(cfg, s.stageIndex).length > 0 && (
                                      <div className="lg:w-[40%] shrink-0 rounded-lg bg-surface-secondary/70 border border-border p-3">
                                        <p className="text-[10px] font-bold uppercase tracking-wider text-txt-tertiary mb-1.5">Covered on the call</p>
                                        <div className="space-y-1">
                                          {obCoveredList(cfg, s.stageIndex).map((item, k) => (
                                            <label key={k} className="flex items-center gap-2 text-[12px] text-txt-primary cursor-pointer">
                                              <input
                                                type="checkbox" checked={!!s.covered?.[k]}
                                                onChange={(e) => {
                                                  const next = obCoveredList(cfg, s.stageIndex!).map((_, j) => (j === k ? e.target.checked : !!s.covered?.[j]))
                                                  obMeetingNotes(ob.id, s.id, { covered: next }).then(() => { loadSessions(ob.id); reload() })
                                                }}
                                                className="w-4 h-4 accent-emerald-600"
                                              />
                                              {item}
                                            </label>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
                {/* Pending-tasks side panel */}
                <div className="w-72 shrink-0 rounded-xl border border-border bg-surface p-3 sticky top-2">
                  <p className="text-[11px] font-bold uppercase tracking-wider text-txt-primary mb-1.5">Pending tasks</p>
                  {pendingBySession.length === 0 && sessions.otherPending.length === 0 ? (
                    <p className="text-[11px] text-txt-tertiary">Nothing pending — all caught up.</p>
                  ) : (
                    <div className="space-y-2.5">
                      {pendingBySession.map(({ s, pending }) => (
                        <div key={s.id}>
                          <button onClick={() => setOpenSession(s.id)} className="text-[10px] font-bold text-brand-600 hover:underline">{fmtD(s.startAt)} — {(s.subject || 'Session').slice(0, 34)}</button>
                          {pending.map((t) => <TaskRow key={`${t.stageIndex}:${t.index}`} t={t} />)}
                        </div>
                      ))}
                      {sessions.otherPending.length > 0 && (
                        <div>
                          <p className="text-[10px] font-bold text-txt-secondary">Added by hand</p>
                          {sessions.otherPending.map((t) => <TaskRow key={`${t.stageIndex}:${t.index}`} t={t} />)}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )
          })()}

        </>
      )}

      {/* NEW GOLD QUOTE modal (Lee, 12 Oct 2026): price + custom payment link,
          every time — payment flips back to pending, the customer's banner
          and Pay now button pick up the new amount/link immediately, and the
          plan-confirmed email goes out with it. */}
      {goldQuoteOpen && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setGoldQuoteOpen(false)}>
          <div className="bg-surface rounded-2xl border border-border shadow-xl p-5 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <p className="text-[15px] font-bold text-txt-primary">New Gold quote — {ob.companyName}</p>
            <p className="text-[11px] text-txt-tertiary mt-0.5 mb-3">Sets the agreed price and the custom Stripe payment link for this quote (Gold always uses a custom link). Payment goes back to pending and the customer sees the new amount + Pay now immediately. Hours top-ups already added carry over.</p>
            <label className="block text-[10px] font-bold uppercase tracking-wider text-txt-tertiary mb-1">Agreed price (£)</label>
            <input value={gqPrice} onChange={(e) => setGqPrice(e.target.value)} placeholder="e.g. 3200" className={`${inputCls} w-full mb-2.5`} />
            <label className="block text-[10px] font-bold uppercase tracking-wider text-txt-tertiary mb-1">Stripe payment link for this quote (required)</label>
            <input value={gqLink} onChange={(e) => setGqLink(e.target.value)} placeholder="https://buy.stripe.com/…" className={`${inputCls} w-full`} />
            <div className="flex justify-end gap-2 mt-4">
              <Button size="sm" variant="secondary" onClick={() => setGoldQuoteOpen(false)}>Cancel</Button>
              <Button size="sm" onClick={() => {
                const n = Number(gqPrice)
                if (!n || n <= 0) { crmToast('Enter the agreed price', 'error'); return }
                const lk = gqLink.trim()
                if (!/^https:\/\/\S+$/.test(lk)) { crmToast('Paste the Stripe payment link for this quote — Gold always uses a custom link', 'error'); return }
                obSetPlan(ob.id, { plan: 'Gold', price: n, paymentLinkUrl: lk })
                  .then(() => { crmToast('Gold quote sent — payment pending with the new amount', 'success'); setGoldQuoteOpen(false); reload() })
                  .catch((e: any) => crmToast(e?.message || 'Failed', 'error'))
              }}>Send quote</Button>
            </div>
          </div>
        </div>
      )}

      {/* Book-meeting mini modal (CRM record; the Calendar bridge is the integrations phase). */}
      {booking && (
        <BookMeetingMini
          stages={cfg.stages.map((s) => s.name)}
          initialStage={booking.stageIndex}
          onClose={() => setBooking(null)}
          onSave={(p) => obCreateMeeting(ob.id, p).then(() => { crmToast('Meeting booked', 'success'); setBooking(null); reload() }).catch(() => crmToast('Could not book', 'error'))}
        />
      )}

      {/* Customer-link popup (Lee, 30 Sep 2026): the link + embed code in one place. */}
      {/* UPGRADE PLAN popup (Lee, 2 Oct 2026): higher plans only; Gold takes
          a custom hours figure. Top-up hours already added carry over. */}
      {upgradeOpen && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setUpgradeOpen(false)}>
          <div className="bg-surface rounded-2xl border border-border shadow-xl p-5 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <p className="text-[15px] font-bold text-txt-primary">Upgrade from {ob.plan}</p>
            <p className="text-[11px] text-txt-tertiary mt-0.5 mb-3">Hours already topped up carry over. Paid upgrades go to payment pending with a fresh link.</p>
            <div className="space-y-2">
              {upgradeTargets.map((name) => {
                const p = cfg.plans[name]
                if (!p) return null
                const isGold = name === 'Gold'
                return (
                  <div key={name} className="rounded-xl border border-border p-3 flex items-center gap-3 flex-wrap">
                    <span className="text-[13px] font-bold text-txt-primary">{name}</span>
                    <span className="text-[11px] text-txt-tertiary">{isGold ? `POA · from £${p.gbp.toLocaleString()}` : `£${p.gbp.toLocaleString()} · ${p.hrs}h`}</span>
                    {isGold && (
                      <input
                        type="number" min={1} max={500} value={upgradeGoldHours}
                        onChange={(e) => setUpgradeGoldHours(e.target.value)}
                        placeholder={`Hours (default ${p.hrs})`}
                        className={`${inputCls} w-36`}
                        title="Gold hours allowance for this customer"
                      />
                    )}
                    <Button
                      size="sm" className="ml-auto"
                      onClick={() => obSetPlan(ob.id, { plan: name, ...(isGold && upgradeGoldHours.trim() ? { hours: Number(upgradeGoldHours) } : {}) })
                        .then(() => { crmToast(`Upgraded to ${name}`, 'success'); setUpgradeOpen(false); reload() })
                        .catch((e: any) => crmToast(e?.message || 'Could not upgrade', 'error'))}
                    >Upgrade to {name}</Button>
                  </div>
                )
              })}
            </div>
            <div className="flex justify-end mt-3">
              <button onClick={() => setUpgradeOpen(false)} className="text-[12px] font-semibold text-txt-tertiary hover:text-txt-primary">Close</button>
            </div>
          </div>
        </div>
      )}
      {linkOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" style={{ background: 'rgba(15,23,42,.5)' }} onClick={() => setLinkOpen(false)}>
          <div className="w-[560px] rounded-xl bg-surface border border-border shadow-xl p-5 space-y-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <p className="text-[15px] font-bold text-txt-primary">Customer link — {ob.companyName}</p>
              <button onClick={() => setLinkOpen(false)} className="text-txt-tertiary hover:text-txt-primary">✕</button>
            </div>
            {link ? (
              <>
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-txt-tertiary mb-1">Direct link</p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 min-w-0 rounded-lg bg-surface-secondary border border-border px-2.5 py-2 text-[11px] text-txt-secondary truncate">{link}</code>
                    <Button size="sm" variant="secondary" onClick={() => copy(link, 'Link')}>Copy</Button>
                  </div>
                </div>
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-txt-tertiary mb-1">Embed code (for their WeQuote Onboarding page)</p>
                  <pre className="rounded-lg bg-surface-secondary border border-border p-2.5 text-[10px] text-txt-secondary overflow-x-auto max-h-40">{embedCode}</pre>
                  <div className="flex justify-end mt-1.5">
                    <Button size="sm" variant="secondary" onClick={() => copy(embedCode, 'Embed code')}>Copy embed</Button>
                  </div>
                </div>
                <div className="flex items-center justify-between pt-1">
                  <button
                    onClick={() => obRotateToken(ob.id).then((r) => { crmToast('Token rotated — old links are dead', 'success'); obGetLink(ob.id).then((l) => setLink(l.url)).catch(() => {}) })}
                    className="text-[11px] font-semibold text-red-600 hover:underline"
                    title="Invalidates every existing link and embed immediately"
                  >Rotate token</button>
                  <a href={link} target="_blank" rel="noreferrer" className="px-3 py-1.5 rounded-lg text-xs font-bold text-white bg-brand-600 hover:bg-brand-700">Customer view →</a>
                </div>
              </>
            ) : (
              <div className="text-center py-4">
                <p className="text-[12px] text-txt-secondary mb-3">No link yet for this record.</p>
                <Button size="sm" onClick={() => obRotateToken(ob.id).then(() => { crmToast('Link generated', 'success'); obGetLink(ob.id).then((l) => setLink(l.url)).catch(() => {}) })}>Generate link</Button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* At Risk panel (Phase 5). */}
      {riskOpen && cfg && (
        <ObRiskPanel
          ob={ob}
          cfg={cfg}
          users={users}
          onClose={() => setRiskOpen(false)}
          onSaved={reload}
          onBookReview={() => { setRiskOpen(false); openCalendly() }}
        />
      )}

      {/* Transcript side sheet (stub until the Meet integration phase). */}
      {transcript && (
        <div className="fixed inset-0 z-[9999]" onClick={() => setTranscript(null)}>
          <div className="absolute inset-0" style={{ background: 'rgba(15,23,42,.4)' }} />
          <div className="absolute right-0 top-0 bottom-0 w-[420px] bg-surface border-l border-border shadow-xl p-5 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <p className="text-[15px] font-bold text-txt-primary">Transcript</p>
              <button onClick={() => setTranscript(null)} className="text-txt-tertiary hover:text-txt-primary">✕</button>
            </div>
            <p className="text-[12px] text-txt-secondary mt-1">{transcript.subject || 'Onboarding call'}</p>
            {transcriptText ? (
              <pre className="mt-4 rounded-lg border border-border bg-surface-secondary/60 p-3 text-[11px] text-txt-primary whitespace-pre-wrap font-sans leading-relaxed max-h-[70vh] overflow-y-auto">{transcriptText}</pre>
            ) : transcript.ref ? (
              <div className="mt-4 space-y-2">
                <p className="text-[12px] text-txt-secondary">The transcript couldn't be shown inline.</p>
                <a href={transcript.ref} target="_blank" rel="noreferrer" className="inline-block px-3 py-1.5 rounded-lg text-xs font-bold text-white bg-brand-600 hover:bg-brand-700">Open transcript ↗</a>
              </div>
            ) : (
              <div className="mt-4 rounded-lg border border-dashed border-border p-4 text-center text-[12px] text-txt-tertiary">
                No transcript for this meeting yet — Google Meet attaches one automatically after processed calls.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

const BookMeetingMini: React.FC<{
  stages: string[]
  initialStage: number | null
  onClose: () => void
  onSave: (p: { stageIndex: number | null; startAt: string; durationMin: number; subject: string | null; attendees: string[] }) => void
}> = ({ stages, initialStage, onClose, onSave }) => {
  const [stage, setStage] = useState<string>(initialStage != null ? String(initialStage) : '')
  const [date, setDate] = useState('')
  const [time, setTime] = useState('10:00')
  const [dur, setDur] = useState('30')
  const [subject, setSubject] = useState('')
  const [emails, setEmails] = useState('')
  const inputCls = 'rounded-lg border border-border bg-surface px-2.5 py-1.5 text-sm text-txt-primary focus:outline-none focus:border-brand-400'
  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" style={{ background: 'rgba(15,23,42,.5)' }} onClick={onClose}>
      <div className="w-[440px] rounded-xl bg-surface border border-border shadow-xl p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
        <p className="text-[15px] font-bold text-txt-primary">Book onboarding meeting</p>
        <select value={stage} onChange={(e) => setStage(e.target.value)} className={`${inputCls} w-full`}>
          <option value="">No specific stage</option>
          {stages.map((s, i) => <option key={s} value={String(i)}>{s}</option>)}
        </select>
        <div className="flex gap-2">
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={`${inputCls} flex-1`} />
          <input type="time" value={time} onChange={(e) => setTime(e.target.value)} className={inputCls} />
          <select value={dur} onChange={(e) => setDur(e.target.value)} className={inputCls}>
            {['15', '30', '45', '60', '90'].map((d) => <option key={d} value={d}>{d} min</option>)}
          </select>
        </div>
        <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject (e.g. Quote Training kick-off)" className={`${inputCls} w-full`} />
        <input value={emails} onChange={(e) => setEmails(e.target.value)} placeholder="Attendee emails, comma-separated" className={`${inputCls} w-full`} />
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="secondary" onClick={onClose}>Cancel</Button>
          <Button size="sm" onClick={() => {
            if (!date) { crmToast('Pick a date', 'error'); return }
            onSave({
              stageIndex: stage === '' ? null : Number(stage),
              startAt: new Date(`${date}T${time}:00`).toISOString(),
              durationMin: Number(dur),
              subject: subject.trim() || null,
              attendees: emails.split(',').map((e) => e.trim()).filter((e) => /\S+@\S+\.\S+/.test(e)),
            })
          }}>Book</Button>
        </div>
      </div>
    </div>
  )
}
